import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  UserCheck,
  FileText,
  ShieldCheck,
  Globe,
  Camera,
  CheckCircle2,
  Landmark,
} from 'lucide-react';

type StepStatus = 'pending' | 'completed';

export interface KycStatusData {
  aadhaarStatus: StepStatus;
  panStatus: StepStatus;
  passportStatus: StepStatus;
  facialStatus: StepStatus;
  bankStatus: StepStatus;
  isKycComplete: boolean;
}

interface KycStatusProps {
  data: KycStatusData;
}

const KycStatus = ({ data }: KycStatusProps) => {
  const {
    aadhaarStatus,
    panStatus,
    passportStatus,
    facialStatus,
    bankStatus,
    isKycComplete,
  } = data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Status</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="mt-1">
            <UserCheck className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900">
              Profile Completed
            </h4>
            <p className="text-xs text-gray-500">
              Make sure your basic profile is up to date.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="mt-1">
            {aadhaarStatus === 'completed' ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <FileText className="h-5 w-5 text-yellow-500" />
            )}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900">
              Aadhaar Verification
            </h4>
            <p className="text-xs text-gray-500">
              {aadhaarStatus === 'completed'
                ? 'Aadhaar verified successfully.'
                : 'Submit your Aadhaar and complete  verification.'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="mt-1">
            {panStatus === 'completed' ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-yellow-500" />
            )}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900">
              PAN Verification
            </h4>
            <p className="text-xs text-gray-500">
              {panStatus === 'completed'
                ? 'PAN verified successfully.'
                : 'Upload your PAN to verify identity.'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="mt-1">
            {passportStatus === 'completed' ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <Globe className="h-5 w-5 text-orange-500" />
            )}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900">
              Passport Verification
            </h4>
            <p className="text-xs text-gray-500">
              {passportStatus === 'completed'
                ? 'Passport verified successfully.'
                : 'Upload your passport for international verification.'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="mt-1">
            {facialStatus === 'completed' ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <Camera className="h-5 w-5 text-purple-600" />
            )}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900">
              Facial Match
            </h4>
            <p className="text-xs text-gray-500">
              {facialStatus === 'completed'
                ? 'Selfie verification complete.'
                : 'Complete selfie verification to finish KYC.'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="mt-1">
            {bankStatus === 'completed' ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <Landmark className="h-5 w-5 text-purple-600" />
            )}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900">
              Bank Verification
            </h4>
            <p className="text-xs text-gray-500">
              {bankStatus === 'completed'
                ? 'Bank details verified successfully.'
                : 'Add your bank details for withdrawals and deposits.'}
            </p>
          </div>
        </div>

        {isKycComplete ? (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">
            Your KYC is complete. You can now trade without limits.
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default KycStatus;
